import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import { useToast } from '../ui/ToastProvider'

const EMPTY_MAPPING = {
    store: 'apple_iap',
    productId: '',
    planCode: '',
    periodMonths: 1,
    currency: 'KRW',
    storePrice: '',
    displayName: '',
    memo: '',
    active: true,
}

const STORE_LABELS = {
    apple_iap: 'App Store',
    google_play: 'Google Play',
    web_pg: 'Web PG',
    toss: 'Toss Payments',
    toss_pg: 'Toss Payments',
    portone: 'PortOne',
    portone_pg: 'PortOne',
    inicis: 'KG Inicis',
    nicepay: 'Nicepay',
    mock: 'Mock PG',
}

export default function BillingConfigPage() {
    const { show } = useToast()
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [plans, setPlans] = useState([])
    const [featurePolicies, setFeaturePolicies] = useState([])
    const [storeProducts, setStoreProducts] = useState([])
    const [newMapping, setNewMapping] = useState(EMPTY_MAPPING)

    const loadConfigs = useCallback(async () => {
        setLoading(true)
        try {
            const [plansRes, featuresRes, storeProductsRes] = await Promise.all([
                api.get('/billing/admin/config/plans'),
                api.get('/billing/admin/config/plan-features'),
                api.get('/billing/admin/config/channel-products'),
            ])
            const loadedPlans = Array.isArray(plansRes.data?.data) ? plansRes.data.data : []
            setPlans(loadedPlans)
            setFeaturePolicies(Array.isArray(featuresRes.data?.data) ? featuresRes.data.data : [])
            setStoreProducts(Array.isArray(storeProductsRes.data?.data) ? storeProductsRes.data.data : [])
            setNewMapping((prev) => ({
                ...prev,
                planCode: prev.planCode || loadedPlans[0]?.planCode || '',
            }))
        } catch (e) {
            show('과금 설정을 불러오지 못했습니다.', 'error')
        } finally {
            setLoading(false)
        }
    }, [show])

    useEffect(() => {
        loadConfigs()
    }, [loadConfigs])

    const featureMap = useMemo(() => {
        return featurePolicies.reduce((acc, row) => {
            acc[row.planCode] = acc[row.planCode] || []
            acc[row.planCode].push(row)
            return acc
        }, {})
    }, [featurePolicies])

    const storeProductMap = useMemo(() => {
        return storeProducts.reduce((acc, row) => {
            acc[row.planCode] = acc[row.planCode] || []
            acc[row.planCode].push(row)
            return acc
        }, {})
    }, [storeProducts])

    const updatePlanField = (planId, field, value) => {
        setPlans((prev) => prev.map((plan) => (
            plan.id === planId ? { ...plan, [field]: value } : plan
        )))
    }

    const updateFeatureField = (planCode, featureCode, field, value) => {
        setFeaturePolicies((prev) => prev.map((row) => (
            row.planCode === planCode && row.featureCode === featureCode
                ? { ...row, [field]: value }
                : row
        )))
    }

    const updateStoreProductField = (id, field, value) => {
        setStoreProducts((prev) => prev.map((row) => (
            row.id === id ? { ...row, [field]: value } : row
        )))
    }

    const savePlan = async (plan) => {
        setSaving(true)
        try {
            await api.put(`/billing/admin/config/plans/${plan.id}`, plan)
            show(`${plan.planCode} 플랜이 저장되었습니다.`, 'success')
            loadConfigs()
        } catch (err) {
            show('플랜 저장에 실패했습니다.', 'error')
        } finally {
            setSaving(false)
        }
    }

    const saveFeatures = async (planVersionId, planCode) => {
        setSaving(true)
        try {
            await api.put(`/billing/admin/config/plan-features/${planVersionId}`, featurePolicies.filter((row) => row.planCode === planCode))
            show(`${planCode} 기능 정책이 저장되었습니다.`, 'success')
            loadConfigs()
        } catch (err) {
            show('기능 정책 저장에 실패했습니다.', 'error')
        } finally {
            setSaving(false)
        }
    }

    const saveStoreProduct = async (mapping) => {
        setSaving(true)
        try {
            await api.put(`/billing/admin/config/channel-products/${mapping.id}`, normalizeMappingPayload(mapping))
            show('스토어 상품 매핑이 저장되었습니다.', 'success')
            loadConfigs()
        } catch (err) {
            show(err?.response?.data?.message || '스토어 상품 매핑 저장에 실패했습니다.', 'error')
        } finally {
            setSaving(false)
        }
    }

    const createStoreProduct = async () => {
        if (!newMapping.productId.trim() || !newMapping.planCode) {
            show('스토어 상품 ID와 플랜을 입력해 주세요.', 'error')
            return
        }
        setSaving(true)
        try {
            await api.post('/billing/admin/config/channel-products', normalizeMappingPayload(newMapping))
            show('스토어 상품 매핑이 추가되었습니다.', 'success')
            setNewMapping({ ...EMPTY_MAPPING, planCode: newMapping.planCode })
            loadConfigs()
        } catch (err) {
            show(err?.response?.data?.message || '스토어 상품 매핑 추가에 실패했습니다.', 'error')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <section>
                <div className="flex items-center justify-center py-12">
                    <div className="text-slate-500">로딩 중...</div>
                </div>
            </section>
        )
    }

    return (
        <section>
            <div className="mb-6 flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold">과금정보관리</h1>
                    <MenuGuideButton slug="subscriptions" title="구독·결제 가이드" anchor="step-02" />
                </div>
            </div>

            <div className="mb-6 rounded-lg border bg-white p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-3">
                    <h2 className="text-lg font-semibold">스토어 상품 매핑 추가</h2>
                    <button
                        className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-brand-navy/90 disabled:opacity-50"
                        onClick={createStoreProduct}
                        disabled={saving}
                    >
                        {saving ? '저장 중...' : '매핑 추가'}
                    </button>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <Field label="스토어">
                        <StoreSelect value={newMapping.store} onChange={(value) => setNewMapping((prev) => ({ ...prev, store: value }))} />
                    </Field>
                    <Field label="상품 ID">
                        <TextInput value={newMapping.productId} onChange={(value) => setNewMapping((prev) => ({ ...prev, productId: value }))} placeholder="sgez.standard.monthly" />
                    </Field>
                    <Field label="내부 플랜">
                        <PlanSelect plans={plans} value={newMapping.planCode} onChange={(value) => setNewMapping((prev) => ({ ...prev, planCode: value }))} />
                    </Field>
                    <Field label="기간">
                        <NumberInput value={newMapping.periodMonths} onChange={(value) => setNewMapping((prev) => ({ ...prev, periodMonths: value }))} />
                    </Field>
                    <Field label="스토어 표시명">
                        <TextInput value={newMapping.displayName} onChange={(value) => setNewMapping((prev) => ({ ...prev, displayName: value }))} placeholder="Standard Monthly" />
                    </Field>
                    <Field label="스토어 가격">
                        <NumberInput value={newMapping.storePrice} onChange={(value) => setNewMapping((prev) => ({ ...prev, storePrice: value }))} allowEmpty />
                    </Field>
                    <Field label="통화">
                        <TextInput value={newMapping.currency} onChange={(value) => setNewMapping((prev) => ({ ...prev, currency: value }))} />
                    </Field>
                    <Field label="상태">
                        <ActiveSelect value={newMapping.active} onChange={(value) => setNewMapping((prev) => ({ ...prev, active: value }))} />
                    </Field>
                </div>
            </div>

            <div className="space-y-6">
                {plans.map((plan) => (
                    <div key={plan.id} className="rounded-lg border bg-white p-6">
                        <div className="mb-6 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold">{plan.planCode}</h2>
                                <p className="mt-1 text-sm text-slate-500">버전 {plan.versionNo} / planVersionId {plan.planVersionId}</p>
                            </div>
                            <button
                                className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-brand-navy/90 disabled:opacity-50"
                                onClick={() => savePlan(plan)}
                                disabled={saving}
                            >
                                {saving ? '저장 중...' : '플랜 저장'}
                            </button>
                        </div>

                        <div className="mb-6">
                            <h3 className="mb-4 border-b pb-2 text-base font-medium">플랜 기본정보</h3>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                <Field label="플랜명">
                                    <TextInput value={plan.name || ''} onChange={(value) => updatePlanField(plan.id, 'name', value)} />
                                </Field>
                                <Field label="월 결제금액">
                                    <NumberInput value={plan.monthlyPrice || 0} onChange={(value) => updatePlanField(plan.id, 'monthlyPrice', value)} />
                                </Field>
                                <Field label="연 결제금액">
                                    <NumberInput value={plan.yearlyPrice || 0} onChange={(value) => updatePlanField(plan.id, 'yearlyPrice', value)} />
                                </Field>
                                <Field label="기본 체험기간">
                                    <NumberInput value={plan.defaultTrialDays || 0} onChange={(value) => updatePlanField(plan.id, 'defaultTrialDays', value)} />
                                </Field>
                                <Field label="설명">
                                    <TextInput value={plan.description || ''} onChange={(value) => updatePlanField(plan.id, 'description', value)} />
                                </Field>
                                <Field label="활성 여부">
                                    <ActiveSelect value={plan.active} onChange={(value) => updatePlanField(plan.id, 'active', value)} />
                                </Field>
                            </div>
                        </div>

                        <StoreProductTable
                            mappings={storeProductMap[plan.planCode] || []}
                            plans={plans}
                            saving={saving}
                            onChange={updateStoreProductField}
                            onSave={saveStoreProduct}
                        />

                        <div>
                            <div className="mb-4 flex items-center justify-between border-b pb-2">
                                <h3 className="text-base font-medium">기능/할당량 정책</h3>
                                <button
                                    className="rounded bg-brand-navy px-4 py-2 text-sm text-white hover:bg-brand-navy/90 disabled:opacity-50"
                                    onClick={() => saveFeatures(plan.planVersionId, plan.planCode)}
                                    disabled={saving}
                                >
                                    {saving ? '저장 중...' : '기능 정책 저장'}
                                </button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-slate-50 text-slate-600">
                                        <tr>
                                            <th className="px-4 py-3 text-left">기능</th>
                                            <th className="px-4 py-3 text-left">사용</th>
                                            <th className="px-4 py-3 text-left">Quota mode</th>
                                            <th className="px-4 py-3 text-left">월 한도</th>
                                            <th className="px-4 py-3 text-left">단위</th>
                                            <th className="px-4 py-3 text-left">초과 정책</th>
                                            <th className="px-4 py-3 text-left">포인트 비용</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(featureMap[plan.planCode] || []).map((feature) => (
                                            <tr key={`${plan.planCode}-${feature.featureCode}`} className="border-t">
                                                <td className="px-4 py-3 font-medium">{feature.featureName}</td>
                                                <td className="px-4 py-3">
                                                    <ActiveSelect compact value={feature.enabled} onChange={(value) => updateFeatureField(plan.planCode, feature.featureCode, 'enabled', value)} />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <select className="rounded border px-2 py-1" value={feature.quotaMode || 'NONE'} onChange={(e) => updateFeatureField(plan.planCode, feature.featureCode, 'quotaMode', e.target.value)}>
                                                        <option value="NONE">NONE</option>
                                                        <option value="LIMITED">LIMITED</option>
                                                        <option value="UNLIMITED">UNLIMITED</option>
                                                    </select>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <NumberInput compact value={feature.monthlyLimit || 0} onChange={(value) => updateFeatureField(plan.planCode, feature.featureCode, 'monthlyLimit', value)} />
                                                </td>
                                                <td className="px-4 py-3">{feature.quotaUnit || '-'}</td>
                                                <td className="px-4 py-3">
                                                    <select className="rounded border px-2 py-1" value={feature.overagePolicy || 'BLOCK'} onChange={(e) => updateFeatureField(plan.planCode, feature.featureCode, 'overagePolicy', e.target.value)}>
                                                        <option value="BLOCK">BLOCK</option>
                                                        <option value="ALLOW_WITH_POINT">ALLOW_WITH_POINT</option>
                                                    </select>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <NumberInput compact value={feature.pointCost || 0} onChange={(value) => updateFeatureField(plan.planCode, feature.featureCode, 'pointCost', value)} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    )
}

function StoreProductTable({ mappings, plans, saving, onChange, onSave }) {
    return (
        <div className="mb-6">
            <div className="mb-4 border-b pb-2">
                <h3 className="text-base font-medium">스토어 상품 매핑</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            <th className="px-4 py-3 text-left">스토어</th>
                            <th className="px-4 py-3 text-left">상품 ID</th>
                            <th className="px-4 py-3 text-left">플랜</th>
                            <th className="px-4 py-3 text-left">기간</th>
                            <th className="px-4 py-3 text-left">가격</th>
                            <th className="px-4 py-3 text-left">상태</th>
                            <th className="px-4 py-3 text-left">저장</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mappings.map((mapping) => (
                            <tr key={mapping.id} className="border-t align-top">
                                <td className="px-4 py-3">
                                    <StoreSelect compact value={mapping.store} onChange={(value) => onChange(mapping.id, 'store', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <TextInput value={mapping.productId || ''} onChange={(value) => onChange(mapping.id, 'productId', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <PlanSelect plans={plans} value={mapping.planCode || ''} onChange={(value) => onChange(mapping.id, 'planCode', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <NumberInput compact value={mapping.periodMonths || 1} onChange={(value) => onChange(mapping.id, 'periodMonths', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-2">
                                        <TextInput compact value={mapping.currency || 'KRW'} onChange={(value) => onChange(mapping.id, 'currency', value)} />
                                        <NumberInput compact allowEmpty value={mapping.storePrice ?? ''} onChange={(value) => onChange(mapping.id, 'storePrice', value)} />
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <ActiveSelect compact value={mapping.active} onChange={(value) => onChange(mapping.id, 'active', value)} />
                                </td>
                                <td className="px-4 py-3">
                                    <button className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50" disabled={saving} onClick={() => onSave(mapping)}>
                                        저장
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {mappings.length === 0 && (
                            <tr className="border-t">
                                <td className="px-4 py-6 text-center text-slate-500" colSpan={7}>등록된 스토어 상품 매핑이 없습니다.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

function normalizeMappingPayload(mapping) {
    return {
        store: mapping.store,
        channel: mapping.store,
        productId: (mapping.productId || '').trim(),
        externalProductId: (mapping.productId || '').trim(),
        planCode: mapping.planCode,
        periodMonths: Number(mapping.periodMonths) || 1,
        currency: mapping.currency || 'KRW',
        storePrice: mapping.storePrice === '' || mapping.storePrice == null ? null : Number(mapping.storePrice),
        displayName: mapping.displayName || '',
        memo: mapping.memo || '',
        active: Boolean(mapping.active),
    }
}

function StoreSelect({ value, onChange, compact = false }) {
    return (
        <select className={`${compact ? 'w-32' : 'w-full'} rounded border px-3 py-2 text-sm`} value={value || 'apple_iap'} onChange={(e) => onChange(e.target.value)}>
            {Object.entries(STORE_LABELS).map(([store, label]) => (
                <option key={store} value={store}>{label}</option>
            ))}
        </select>
    )
}

function PlanSelect({ plans, value, onChange }) {
    return (
        <select className="w-full rounded border px-3 py-2 text-sm" value={value || ''} onChange={(e) => onChange(e.target.value)}>
            <option value="">선택</option>
            {plans.map((plan) => (
                <option key={plan.planCode} value={plan.planCode}>{plan.name || plan.planCode}</option>
            ))}
        </select>
    )
}

function ActiveSelect({ value, onChange, compact = false }) {
    return (
        <select className={`${compact ? 'w-24' : 'w-full'} rounded border px-3 py-2 text-sm`} value={value ? 'true' : 'false'} onChange={(e) => onChange(e.target.value === 'true')}>
            <option value="true">활성</option>
            <option value="false">비활성</option>
        </select>
    )
}

function TextInput({ value, onChange, placeholder = '', compact = false }) {
    return (
        <input className={`${compact ? 'w-20' : 'w-full'} rounded border px-3 py-2 text-sm`} value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    )
}

function NumberInput({ value, onChange, compact = false, allowEmpty = false }) {
    return (
        <input
            type="number"
            className={`${compact ? 'w-24' : 'w-full'} rounded border px-3 py-2 text-sm`}
            value={value ?? ''}
            onChange={(e) => onChange(allowEmpty && e.target.value === '' ? '' : Number(e.target.value) || 0)}
        />
    )
}

function Field({ label, children }) {
    return (
        <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
            {children}
        </div>
    )
}
